// tslint:disable:max-line-length
import { TCI_MARKETPLACE_LISTING_VIEW_MODE, TCI_MARKETPLACE_PRICING_OPTIONS, TCI_MARKETPLACE_LISTING_TYPE } from "@tci/common";
import { Decision, BoolTable, Truth } from "booltable";
import { DecisionMonad, BoolTableMonad } from "booltable/dist/types";
import {
    IMkpV2ListContext,
    IMkpV2ListEvalRes,
    CONDITION_IS_ALLOWED_IN,
    CONDITION_STATUS_MANAGING,
    CONDITION_STATUS_CONSUMING,
    MKP_FOOTER_ITEMS,
    formatResult,
    ConditionsForStatusManaging,
    ConditionsForStatusConsuming
} from "./listing-bt-evaluations";
import { isEmpty } from "lodash";
import { CheckPublic, CheckPendingApproval, CheckAccessRequestCount, CheckPrivate } from "./listing-bt-publishing";
import { CheckCategoryIcon } from "./listing-bt-main";

//#region - Conditions catalog binding condition string with the actual evaluation
const ConditionsForAllowed = (context: IMkpV2ListContext): BoolTableMonad =>
    BoolTable.of([
        [
            CONDITION_IS_ALLOWED_IN.LIST_CARD,
            Truth.of([
                [
                    MKP_FOOTER_ITEMS.CATEGORY_ICON,
                    MKP_FOOTER_ITEMS.PRICING_INFO,
                    MKP_FOOTER_ITEMS.REQUEST_REJECTED_STATUS,
                    MKP_FOOTER_ITEMS.ACCESS_REVOKED_STATUS,
                    MKP_FOOTER_ITEMS.RATING_VIEW_RO,
                    MKP_FOOTER_ITEMS.INSTALLED_STATUS,
                    MKP_FOOTER_ITEMS.STATUS_PUBLIC,
                    MKP_FOOTER_ITEMS.STATUS_PENDING_APPROVAL,
                    MKP_FOOTER_ITEMS.STATUS_ACCESS_REQUEST_COUNT,
                    MKP_FOOTER_ITEMS.STATUS_PRIVATE
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.DETAIL,
            Truth.of([
                [
                    MKP_FOOTER_ITEMS.PROVIDER_NAME,
                    MKP_FOOTER_ITEMS.INSTALLED_STATUS,
                    MKP_FOOTER_ITEMS.DESCRIPTION_LABEL_FULL,
                    MKP_FOOTER_ITEMS.ADDITIONAL_INFO_LINK,
                    MKP_FOOTER_ITEMS.MEDIA,
                    MKP_FOOTER_ITEMS.EULA_LINK,
                    MKP_FOOTER_ITEMS.STATUS_PUBLIC,
                    MKP_FOOTER_ITEMS.STATUS_PENDING_APPROVAL,
                    MKP_FOOTER_ITEMS.STATUS_ACCESS_REQUEST_COUNT,
                    MKP_FOOTER_ITEMS.STATUS_PRIVATE,
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_CARD,
            Truth.of([
                [
                    MKP_FOOTER_ITEMS.CATEGORY_ICON,
                    MKP_FOOTER_ITEMS.RATING_VIEW_RO,
                    MKP_FOOTER_ITEMS.STATUS_PRIVATE
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL,
            Truth.of([
                [
                    MKP_FOOTER_ITEMS.PROVIDER_NAME,
                    MKP_FOOTER_ITEMS.DESCRIPTION_LABEL_FULL,
                    MKP_FOOTER_ITEMS.STATUS_PRIVATE,
                    MKP_FOOTER_ITEMS.ADDITIONAL_INFO_LINK,
                    MKP_FOOTER_ITEMS.MEDIA,
                    MKP_FOOTER_ITEMS.EULA_LINK
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ]
    ]);
//#endregion

// Use this function to see if an item is allowed in a certain mode
const CommonAllowanceCheck = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    let condTable: DecisionMonad;
    switch (context.mode) {
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.LIST_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.DETAIL), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL), { visible: true }]
            ]);
            break;
    }

    return condTable.run("any").chain(formatResult);
};

// Use this function to see if context is in preview mode
const isPreviewMode = (context: IMkpV2ListContext): boolean => {
    return [
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD,
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL
    ].includes(context.mode as any);
};

const CheckProviderName = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    return CommonAllowanceCheck(context);
};

const CheckDescriptionLabelFull = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    return CommonAllowanceCheck(context);
};

const CheckRequestRejectedStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status
    if (allowanceResult.visible) {
        if (isPreviewMode(context)) { // PREVIEW modes skip all status evaluations
            return allowanceResult;
        } else {
            const condTable = Decision.of([
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED), { visible: true }]
            ]);
            return condTable.run("any").chain(formatResult);
        }
    }
    return allowanceResult;
};

const CheckInstalledStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    allowanceResult.visible = allowanceResult.visible &&
        context.asset.listingType !== TCI_MARKETPLACE_LISTING_TYPE.Accelerator; // IPAS-17143 - Not for Accelerator

    // If visible by mode then check by status
    if (allowanceResult.visible) {
        if (isPreviewMode(context)) { // PREVIEW modes skip all status evaluations
            return allowanceResult;
        } else {
            const condTable = Decision.of([
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), { visible: true }]
            ]);
            return condTable.run("any").chain(formatResult);
        }
    }
    return allowanceResult;
};

const CheckAccessRevokedStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status
    if (allowanceResult.visible) {
        if (isPreviewMode(context)) { // PREVIEW modes skip all status evaluations
            return allowanceResult;
        } else {
            const condTable = Decision.of([
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED), { visible: true }]
            ]);
            return condTable.run("any").chain(formatResult);
        }
    }
    return allowanceResult;
};

/**
 * CheckAdditionalInfoLink have to check if the "infoUrl" string is present
 * UT have to check for both cases
 * @param context
 */
const CheckAdditionalInfoLink = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    if (isEmpty(context.asset.infoUrl)) {
        return { visible: false };
    }

    return CommonAllowanceCheck(context);
};

/**
* CheckMedia have to check if the "eulaUrl" string is present
* UT have to check for both cases
* @param context
*/
const CheckMedia = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    if (isEmpty(context.asset.mediaAssets)) {
        return { visible: false };
    }

    return CommonAllowanceCheck(context);
};

/**
 * CheckEULALink have to check if the "eulaUrl" string is present
 * UT have to check for both cases
 * @param context
 */
const CheckEULALink = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    if (context.asset.enabledByDefault || context.asset.managingOrgId === context.asset.hostingOrgId) { // IPAS-17236 - No EULA for OOTB, IPAS-17226 - No EULA for TIBCO
        return { visible: false };
    }

    return CommonAllowanceCheck(context);
};

const CheckRatingViewRO = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    return CommonAllowanceCheck(context); // Ratings all the time for card
};

const CheckPricing = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // Break if pricing info is not Free
    allowanceResult.visible = allowanceResult.visible &&
        context.asset.pricingInfo.pricingOptions !== TCI_MARKETPLACE_PRICING_OPTIONS.Free;

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), { visible: true }],

            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_PENDING_REQUEST), { visible: true, enabled: true }], // IPAS-17198
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_REQUEST), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_APPROVED), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_GET), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REVOKE_AKNOWLEDGED), { visible: true, enabled: true }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckOverFooterItem = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    switch (context.item) {
        case MKP_FOOTER_ITEMS.PROVIDER_NAME:
            return CheckProviderName(context);
        case MKP_FOOTER_ITEMS.DESCRIPTION_LABEL_FULL:
            return CheckDescriptionLabelFull(context);
        case MKP_FOOTER_ITEMS.REQUEST_REJECTED_STATUS:
            return CheckRequestRejectedStatus(context);
        case MKP_FOOTER_ITEMS.INSTALLED_STATUS:
            return CheckInstalledStatus(context);
        case MKP_FOOTER_ITEMS.ACCESS_REVOKED_STATUS:
            return CheckAccessRevokedStatus(context);
        case MKP_FOOTER_ITEMS.ADDITIONAL_INFO_LINK:
            return CheckAdditionalInfoLink(context);
        case MKP_FOOTER_ITEMS.MEDIA:
            return CheckMedia(context);
        case MKP_FOOTER_ITEMS.EULA_LINK:
            return CheckEULALink(context);
        case MKP_FOOTER_ITEMS.RATING_VIEW_RO:
            return CheckRatingViewRO(context);
        case MKP_FOOTER_ITEMS.PRICING_INFO:
            return CheckPricing(context);

        /**
         * The Category Icon check follow the same rules as in "listing-bt-main.ts" file
         */
        case MKP_FOOTER_ITEMS.CATEGORY_ICON:
            return CheckCategoryIcon(context, CommonAllowanceCheck);

        /**
         * The Status checks follow the same rules as in "listing-bt-publishing.ts" file
         */
        case MKP_FOOTER_ITEMS.STATUS_PUBLIC:
            return CheckPublic(context, CommonAllowanceCheck);
        case MKP_FOOTER_ITEMS.STATUS_PRIVATE:
            return CheckPrivate(context, CommonAllowanceCheck);
        case MKP_FOOTER_ITEMS.STATUS_PENDING_APPROVAL:
            return CheckPendingApproval(context, CommonAllowanceCheck);
        case MKP_FOOTER_ITEMS.STATUS_ACCESS_REQUEST_COUNT:
            return CheckAccessRequestCount(context, CommonAllowanceCheck);
    }
};
